import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { workspaceService } from '@/services'
import { Workspace, User } from '@/types'
import { useAuth } from '@/hooks/useAuth'
import {
  PageContainer,
  PageHeader,
  ListPageLayout,
  LoadingState,
  MembersList,
} from '@/components/layout'

export default function WorkspaceMembersPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [members, setMembers] = useState<User[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [removingUserId, setRemovingUserId] = useState<string | null>(null)

  const loadWorkspaceMembers = useCallback(async () => {
    if (!workspaceId) return

    try {
      const data = await workspaceService.getById(workspaceId)
      if (data) {
        setWorkspace(data)
        setMembers(data.users)
      } else {
        toast.error('Workspace not found')
        navigate('/workspaces')
      }
    } catch {
      toast.error('Failed to load workspace members')
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId, navigate])

  useEffect(() => {
    loadWorkspaceMembers()
  }, [loadWorkspaceMembers])

  const handleRemoveMember = async (userId: string) => {
    if (!workspace || !currentUser) return

    // Check if user is the creator
    if (workspace.creator.id !== currentUser.id) {
      toast.error('Only the workspace creator can remove members')
      return
    }

    if (userId === workspace.creator.id) {
      toast.error('Cannot remove the workspace creator')
      return
    }

    if (!confirm('Are you sure you want to remove this member from the workspace?')) {
      return
    }

    setRemovingUserId(userId)
    try {
      await workspaceService.removeUser(workspace.id, userId)
      toast.success('Member removed successfully')
      await loadWorkspaceMembers()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove member')
    } finally {
      setRemovingUserId(null)
    }
  }

  const isCreator = currentUser && workspace && workspace.creator.id === currentUser.id

  if (isLoading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState text="Loading members..." />
      </PageContainer>
    )
  }

  if (!workspace) {
    return null
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="Workspace Members"
        description={`${members.length} ${members.length === 1 ? 'member' : 'members'} in ${workspace.name}`}
        customActions={
          isCreator && (
            <Button onClick={() => navigate(`/workspaces/${workspace.id}/invite`)} size="sm">
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Members
            </Button>
          )
        }
      />

      <ListPageLayout
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search members..."
      >
        <MembersList
          members={members}
          searchQuery={searchQuery}
          creatorId={workspace.creator.id}
          currentUserId={currentUser?.id || ''}
          isCreator={!!isCreator}
          removingUserId={removingUserId}
          onRemoveMember={handleRemoveMember}
          emptyStateMessage="No members yet"
        />
      </ListPageLayout>
    </PageContainer>
  )
}
